
import { CreateElement } from 'vue';
import { Component, InjectReactive, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { FilterProductionCountry, FilterRuntime, FilterVoteCount } from '@/lazy-load-components';

// sub-components specific application modules
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import ResetButton from '@/components/buttons/ResetButton.vue';
import FilterAgeCertification from '@/components/filter-bar/FilterAgeCertification.vue';
import FilterBarContentType from '@/components/filter-bar/FilterBarContentType.vue';
import FilterBarContentTypeSeoFriendly from '@/components/filter-bar/FilterBarContentTypeSeoFriendly.vue';
import FilterBarGenres from '@/components/filter-bar/FilterBarGenres.vue';
import FilterBarProvider from '@/components/filter-bar/FilterBarProvider.vue';
import FilterBarProviderSeoFriendly from '@/components/filter-bar/FilterBarProviderSeoFriendly.vue';
import FilterButton from '@/components/filter-bar/FilterButton.vue';
import FilterMonetizationTypes from '@/components/filter-bar/FilterMonetizationTypes.vue';
import FilterPresentationTypes from '@/components/filter-bar/FilterPresentationTypes.vue';
import FilterPrice from '@/components/filter-bar/FilterPrice.vue';
import FilterRating from '@/components/filter-bar/FilterRating.vue';
import FilterReleaseYear from '@/components/filter-bar/FilterReleaseYear.vue';
import ChipButton from '@/ui-components/ChipButton.vue';

import { CollectionType } from '@/enums/collection-type';

import Provider from '@/interfaces/provider';
import { TitleListName } from '@/interfaces/responses/title-list';

import { FilterValues, getUsedFilters, isAnyFilterActive } from '@/helpers/filter-helper';
import { showProFeatures } from '@/helpers/pro-web-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { calculateScreenWidth } from '@/helpers/ui-helper';

import { WebLocale } from '@/enums/web-locale';
import { providerPageExpTrackEvent } from '@/helpers/experiments/popular-provider-page-exp-helper';
import { AgeCertifications, AgeCertificationsConstant } from '@/stores/constant.store';
import { FilterCollection, FilterName } from '@/stores/filter.store';
import { TitleListStoreStateItem } from '@/stores/title-list.store';
import { UserSettings } from '@/stores/user.store';

const FreeButtonFilter = () => import('./FreeButtonFilter.vue');

const Constant = namespace('constant');
const Routing = namespace('routing');
const TitleListStore = namespace('titleList');
const User = namespace('user');
const Filter = namespace('filter');

const filterValues = { ...FilterValues };

type FilterType<T> = { name: string; filters: T };
type FilterComponents = AdditionalFilters[keyof AdditionalFilters] extends infer Value
	? Value extends { filters: infer Filters }
		? Filters
		: undefined
	: never;

interface AdditionalFilters {
	releaseYear: FilterType<[typeof FilterReleaseYear]>;
	genres: FilterType<[typeof FilterBarGenres]>;
	price: FilterType<[typeof FilterPrice, typeof FilterMonetizationTypes, typeof FilterPresentationTypes]>;
	rating: FilterType<[typeof FilterRating, typeof FilterVoteCount?]>;
	prodCountries?: FilterType<[typeof FilterProductionCountry]>;
	runTime?: FilterType<[typeof FilterRuntime]>;
	ageRating?: FilterType<[typeof FilterAgeCertification]>;
}

@Component({
	components: {
		HiddenHorizontalScrollbar,
		ChipButton,
		FilterAgeCertification,
		FilterBarContentType,
		FilterBarContentTypeSeoFriendly,
		FilterRuntime,
		FilterVoteCount,
		FilterBarGenres,
		FilterBarProvider,
		FilterBarProviderSeoFriendly,
		FilterButton,
		FilterPrice,
		FilterReleaseYear,
		FilterMonetizationTypes,
		FilterPresentationTypes,
		FilterProductionCountry,
		FilterRating,
		ResetButton,
		FreeButtonFilter,
	},
})
export default class FilterBar extends Vue {
	openedPopoverKey: keyof AdditionalFilters | null = null;
	isMounted = false;

	@Prop() collectionType: CollectionType;
	@Prop() currentFilters: FilterCollection;
	@Prop() isFiltersVisible: boolean;
	@Prop() webLocale: WebLocale;
	@Prop() userSettings: UserSettings;
	// START POPULAR-PROVIDER-PAGE-EXP
	@Prop({ default: true }) showContentType: boolean;
	@Prop({ default: false }) showFiltersOnMObile: boolean;
	// END POPULAR-PROVIDER-PAGE-EXP

	@Prop({ default: false }) showFreeButton: boolean;

	@Constant.Getter providers: (filterUserProviders: boolean) => Provider[];

	@Routing.State activeRoute: any;

	@TitleListStore.State(TitleListName.WATCHLIST) watchlistItems: TitleListStoreStateItem;

	@User.Getter isPremium: boolean;

	@Filter.Action toggle: (payload: { filterName: FilterName; value: any; selectedState: 1 | 0 }) => void;

	@InjectReactive(AgeCertificationsConstant) ageCertifications: AgeCertifications;

	get isMobileViewport() {
		return this.isMounted && calculateScreenWidth() < 768;
	}

	/**
	 * helper to determine the affected filter keys.
	 * omit filterComponent to get all possible affected filter keys.
	 */
	get additionalFilters() {
		const baseFilters: AdditionalFilters = {
			releaseYear: { name: this.$tc('WEBAPP_RELEASEYEAR'), filters: [FilterReleaseYear] },
			genres: { name: this.$tc('WEBAPP_GENRES'), filters: [FilterBarGenres] },
			price: {
				name: this.$tc('WEBAPP_PRICE'),
				filters: [FilterPrice, FilterMonetizationTypes, FilterPresentationTypes],
			},
			rating: { name: this.$tc('WEBAPP_RATING'), filters: [FilterRating] },
		};

		if (showProFeatures(this.webLocale)) {
			baseFilters.prodCountries = {
				name: this.$tc('WEBAPP_TITLE_DETAIL_PROD_COUNTRIES'),
				filters: [FilterProductionCountry],
			};
			baseFilters.runTime = { name: this.$tc('WEBAPP_RUNTIME'), filters: [FilterRuntime] };

			// if premium user - add imdb popularity filter to the popover
			baseFilters.rating.filters.concat(FilterVoteCount);
		}

		// decide if the age rating should be added to all additional filters
		if (this.ageCertifications.hasCertifications) {
			baseFilters.ageRating = { name: this.$tc('WEBAPP_AGERATING'), filters: [FilterAgeCertification] };
		}

		return baseFilters;
	}

	get filterValues() {
		if (!this.ageCertifications.hasCertifications) {
			delete filterValues['FilterAgeCertification'];
		}
		return filterValues;
	}

	/**
	 * return true if filterbar needs to be hidden
	 *
	 * @returns {boolean}
	 */
	get hideFilterBar(): boolean {
		const routeHasNoFilterBar = !this.activeRoute?.meta?.hasFilterBar;
		const isWatchlistAndEmpty =
			this.activeRoute?.meta.collectionType === CollectionType.WATCHLIST &&
			Object.keys(this.watchlistItems ?? {}).length === 0;
		const watchListAndHideFilter = this.isWatchListTab && !this.isFiltersVisible;
		const searchPageAndHideFilter = this.isSearchTab && !this.isFiltersVisible;

		return searchPageAndHideFilter || watchListAndHideFilter || routeHasNoFilterBar || isWatchlistAndEmpty;
	}

	get isSearchTab(): boolean {
		return this.activeRoute?.meta?.collectionType === CollectionType.SEARCH;
	}

	get isWatchListTab(): boolean {
		return this.activeRoute?.meta?.collectionType === CollectionType.WATCHLIST;
	}

	get userProviders() {
		return this.providers(true);
	}

	get hasSeoFriendlyAppHeader() {
		return this.webLocale === WebLocale.ec;
	}

	get newPageType() {
		return this.activeRoute?.meta?.newPageType;
	}

	mounted() {
		this.isMounted = true;
	}

	hasActiveFilter(item?: FilterComponents) {
		const usedFilters = getUsedFilters(this.filterValues, item);
		return isAnyFilterActive(usedFilters, this.currentFilters);
	}

	async openPopover(additionalFilterKey: keyof AdditionalFilters, event: Event) {
		this.openedPopoverKey = additionalFilterKey;

		const { popoverController } = await import('@/ionic.loader');
		const modal = await popoverController.create({
			cssClass: 'popover__filter',
			component: {
				render: (h: CreateElement) => {
					const addFilters = this.additionalFilters[additionalFilterKey];

					if (addFilters?.filters == null) {
						return h('div', [h('div', { style: { padding: '10px' } }, 'N/A no filters yet')]);
					}

					return h(
						'div',
						addFilters.filters.map(item =>
							h(item, {
								props: {
									collectionType: this.collectionType,
									currentFilters: this.currentFilters,
								},
							})
						)
					);
				},
			},
			componentProps: { parent: this },
			event,
			mode: 'ios',
		} as any);
		modal.present();

		const contexts = [];
		if (this.isPremium) {
			const { getFreemiumContext } = await import('@/helpers/iap-helper');
			contexts.push(getFreemiumContext());
		}

		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'filter_popover_toggled',
				label: additionalFilterKey,
			},
			contexts
		);
		providerPageExpTrackEvent({ action: 'click', label: 'filter_popover_toggled' });

		await modal.onWillDismiss();
		this.openedPopoverKey = null;
	}
}
